import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Page,
  Table,
  type Column,
  Pagination,
  Text,
  DateTimeField,
  EmptyState,
  Badge,
} from "@marekl/adminer/components";
import { useConfirm } from "@marekl/adminer/hooks";
import { notifySuccess } from "@marekl/adminer/api";
import { formatNumber } from "@marekl/adminer/format";
import { useIsAdmin } from "../../auth/useAuth";
import {
  isBatchListLive,
  useBatchList,
  useCancelBatch,
  type Batch,
} from "../../lib/queries/batches";
import { usePagedList } from "../../lib/usePagedList";
import { RefreshButton } from "../../components/RefreshButton";
import styles from "./BatchesPage.module.css";

function progressSummary(b: Batch): string {
  const parts: string[] = [
    `${formatNumber(b.counts.sent)}/${formatNumber(b.total)} sent`,
  ];
  if (b.counts.failed > 0) parts.push(`${formatNumber(b.counts.failed)} failed`);
  if (b.counts.pending + b.counts.leased > 0)
    parts.push(`${formatNumber(b.counts.pending + b.counts.leased)} queued`);
  if (b.counts.cancelled > 0)
    parts.push(`${formatNumber(b.counts.cancelled)} cancelled`);
  if (b.counts.suppressed > 0)
    parts.push(`${formatNumber(b.counts.suppressed)} suppressed`);
  if (b.counts.expired > 0)
    parts.push(`${formatNumber(b.counts.expired)} expired`);
  return parts.join(", ");
}

function batchStatus(b: Batch) {
  const remaining = b.counts.pending + b.counts.leased;
  if (remaining > 0) return <Badge weight="ghost" variant="info" dot>In progress</Badge>;
  if (b.counts.failed > 0) return <Badge weight="ghost" variant="err" dot>With failures</Badge>;
  return <Badge weight="ghost" variant="ok" dot>Completed</Badge>;
}

/** Everything this page shows, for the header's refresh button. */
const REFRESH_KEYS = [["batches"]];

export function BatchesPage() {
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Read only so the watch can stand down off the first page; the cursor
  // itself stays the lib pagination hook's business.
  const cursor = searchParams.get("cursor") ?? "";
  const confirm = useConfirm();
  const cancelBatch = useCancelBatch();

  const { data, isLoading, error, refetch, pagination, summary } = usePagedList(
    useBatchList,
    {},
    {
      searchParams,
      setSearchParams,
      nounForms: ["batch", "batches", "batches"],
    },
  );
  const items = data?.items ?? [];
  const live = isBatchListLive({ cursor: cursor || undefined }, items);

  function confirmCancel(batch: Batch) {
    void confirm({
      title: "Cancel remaining messages",
      description: (
        <>
          Cancel all not-yet-sent messages of batch{" "}
          <strong>{batch.name || batch.id}</strong> (
          {formatNumber(batch.counts.pending)} pending)? Messages that were
          already sent stay unchanged.
        </>
      ),
      confirmLabel: "Cancel remaining",
      variant: "danger",
      onConfirm: async () => {
        await cancelBatch.mutateAsync({ id: batch.id });
      },
    }).then((confirmed) => {
      if (confirmed) notifySuccess("The batch's remaining messages were cancelled.");
    });
  }

  const columns: Column<Batch>[] = [
    {
      key: "created_at",
      header: "Created",
      render: (row) => (
        <Text variant="mono">
          <DateTimeField record={row} source="created_at" />
        </Text>
      ),
    },
    {
      key: "name",
      header: "Name",
      primary: true,
      render: (row) => row.name || <Text variant="muted">(unnamed)</Text>,
    },
    {
      key: "status",
      header: "Status",
      render: (row) => batchStatus(row),
    },
    {
      key: "progress",
      header: "Progress",
      render: (row) => <span className={styles.progress}>{progressSummary(row)}</span>,
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <Table.Actions>
          <Table.Action
            onClick={() => navigate(`/messages?batch_id=${encodeURIComponent(row.id)}`)}
          >
            Messages
          </Table.Action>
          {isAdmin && row.counts.pending > 0 && (
            <Table.Action variant="danger" onClick={() => confirmCancel(row)}>
              Cancel remaining
            </Table.Action>
          )}
        </Table.Actions>
      ),
    },
  ];

  return (
    <Page>
      <Page.Header
        title="Batches"
        subtitle="bulk sends and their progress"
        subtitleVariant="tagline"
        actions={<RefreshButton queryKeys={REFRESH_KEYS} live={live} />}
      />
      <Page.Content stack gap="lg">
        <Table<Batch>
          striped
          cards
          columns={columns}
          rows={items}
          rowKey={(r) => r.id}
          isLoading={isLoading}
          error={error}
          onRetry={refetch}
          empty={
            <EmptyState
              title="No batches"
              description="No bulk send has been created yet."
            />
          }
        />
      </Page.Content>
      <Page.Footer>
        <Pagination {...pagination} summary={summary} />
      </Page.Footer>
    </Page>
  );
}
